import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiArrowLeft,
  FiExternalLink,
  FiShield,
  FiLayers,
  FiCheckSquare,
  FiEye,
  FiMail,
  FiSearch,
  FiArrowRight,
} from 'react-icons/fi';
import SEO from '../../components/SEO';
import './CaseStudy.css';

/**
 * AIShadowCaseStudy — second hand-crafted case study page under
 * /portfolio/aishadow. Same shared CaseStudy.css scaffolding as
 * HomeTaxPro; only the content + --case-study-accent CSS variable
 * differ per project.
 */
const ACCENT = '#10B981';
const LIVE_URL = 'https://aishadow.ai/';
const IMG_MAC =
  'https://silverfoxmedia.s3.amazonaws.com/aishadowmacbook.png';
const IMG_PHONE =
  'https://silverfoxmedia.s3.amazonaws.com/AI%20Shadow%E2%84%A2%20%20Enterprise%20AI%20Governance%20%26%20DLP-portrait.png';

const AIShadowCaseStudy = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'AI Shadow — Case Study',
    headline: 'Enterprise AI Governance & DLP — Positioning + Site Build',
    description:
      'How SilverFox Media positioned and built the AI Shadow marketing site: framework-aligned messaging (NIST AI RMF, ISO 42001, SOC 2, GDPR, HIPAA), a consent-first GA4 + Consent Mode v2 analytics stack, structured-data SEO, and a SendGrid-wired lead-capture pipeline.',
    image: IMG_MAC,
    url: 'https://silverfoxmedia.co/portfolio/aishadow',
    author: {
      '@type': 'Organization',
      name: 'SilverFox Media',
      url: 'https://silverfoxmedia.co',
    },
    about: [
      { '@type': 'Thing', name: 'Enterprise AI Governance' },
      { '@type': 'Thing', name: 'Data Loss Prevention' },
      { '@type': 'Thing', name: 'Google Consent Mode v2' },
      { '@type': 'Thing', name: 'NIST AI RMF' },
      { '@type': 'Thing', name: 'ISO 42001' },
    ],
  };

  return (
    <>
      <SEO
        title="AI Shadow Case Study: Enterprise AI Governance Site Build"
        description="How SilverFox Media positioned and shipped AI Shadow: an enterprise AI governance and DLP marketing site with framework-aligned messaging (NIST AI RMF, ISO 42001, SOC 2, GDPR, HIPAA), Google Consent Mode v2, structured-data SEO, and a SendGrid lead-capture pipeline."
        keywords="AI Shadow case study, enterprise AI governance, AI DLP platform, NIST AI RMF, ISO 42001, Google Consent Mode v2, GA4 privacy, JSON-LD SEO, SendGrid contact modal, static marketing site"
        image="https://silverfoxmedia.co/images/og/aishadow.jpg"
        url="/portfolio/aishadow"
        type="article"
        schema={structuredData}
      />

      <article
        className="case-study"
        style={{ '--case-study-accent': ACCENT }}
      >
        {/* HERO — 2-column on desktop (text left, media right).
            On mobile the hero-media block is hidden via CSS and the
            standalone Featured Image section below handles the visual. */}
        <section className="case-study__hero">
          <div className="container">
            <div className="case-study__hero-grid">
              <div className="case-study__hero-inner">
                <Link to="/portfolio" className="case-study__back">
                  <FiArrowLeft />
                  Back to Portfolio
                </Link>

                <span className="case-study__category">Enterprise SaaS</span>

                <h1 className="case-study__title">
                  AI Shadow:{' '}
                  <span className="case-study__title-accent">
                    Enterprise AI Governance
                  </span>
                </h1>

                <p className="case-study__tagline">
                  Positioning, design, and full marketing site build for an enterprise
                  AI governance and DLP platform. Framework-aligned messaging aimed at
                  NIST AI RMF, ISO 42001, SOC 2, GDPR, and HIPAA buyers — with a
                  consent-first analytics stack and a SendGrid-wired lead pipeline
                  behind every page.
                </p>

                <div className="case-study__hero-cta">
                  <a
                    href={LIVE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="case-study__cta-primary"
                  >
                    Visit Live Site <FiExternalLink />
                  </a>
                  <Link to="/contact" className="case-study__cta-secondary">
                    Start Your Project <FiArrowRight />
                  </Link>
                </div>

                <div className="case-study__hero-meta">
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Client</span>
                    <span className="case-study__hero-meta-value">AI Shadow</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Industry</span>
                    <span className="case-study__hero-meta-value">Enterprise AI Security</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Timeline</span>
                    <span className="case-study__hero-meta-value">2026 — Ongoing</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Role</span>
                    <span className="case-study__hero-meta-value">Positioning + Full Site Build</span>
                  </div>
                </div>
              </div>

              <div className="case-study__hero-media">
                <img
                  src={IMG_MAC}
                  alt="AI Shadow marketing site displayed on a MacBook mockup"
                  className="case-study__hero-macbook"
                  loading="lazy"
                />
                <img
                  src={IMG_PHONE}
                  alt="AI Shadow hero on a portrait mobile screen"
                  className="case-study__hero-phone"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* FEATURED IMAGE */}
        <section className="case-study__featured">
          <div className="container">
            <div className="case-study__featured-image-wrap">
              <img
                src={IMG_MAC}
                alt="AI Shadow marketing site displayed on a MacBook mockup"
                className="case-study__featured-image"
                loading="lazy"
              />
              <img
                src={IMG_PHONE}
                alt="AI Shadow hero on a portrait mobile screen"
                className="case-study__featured-phone"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* OVERVIEW */}
        <section className="case-study__section">
          <div className="container">
            <p className="case-study__section-label">The Overview</p>
            <h2 className="case-study__section-title">
              Selling AI governance to buyers who haven't budgeted for it yet.
            </h2>
            <div className="case-study__prose">
              <p>
                AI Shadow is an enterprise AI governance and DLP platform — multi-layer
                enforcement across a HITL browser layer, an AI DLP backend, and an AI
                Gateway network, backed by a patent-pending actuarial risk-scoring
                engine. The category is new enough that most enterprise security
                leaders haven't yet earmarked spend for it, which shapes every
                messaging and site-architecture decision.
              </p>
              <p>
                Our job wasn't just to build a marketing site — it was to give AI
                Shadow the credibility and audit signal that lets a CISO forward the
                URL to a compliance officer without a follow-up conversation. That
                meant framework-first messaging, structured-data SEO built for AI
                citations, and an analytics stack that would survive a privacy audit
                from day one.
              </p>
            </div>
          </div>
        </section>

        {/* CHALLENGE */}
        <section className="case-study__section" style={{ background: 'var(--color-gray-100)' }}>
          <div className="container">
            <p className="case-study__section-label">The Challenge</p>
            <h2 className="case-study__section-title">
              Credibility for a new category, compliance for a picky buyer.
            </h2>
            <div className="case-study__prose">
              <p>
                Enterprise buyers evaluating an AI governance platform don't just want
                a product — they want signal that the vendor understands the
                regulatory landscape they operate in. The site had to demonstrate
                fluency with NIST AI RMF, ISO 42001, SOC 2, GDPR, and HIPAA without
                turning into a wall of jargon. Every headline, every alt-text, every
                schema property was written with a compliance reviewer in mind.
              </p>
              <p>
                The analytics stack had to earn its own credibility. Shipping a
                privacy-focused enterprise product with a marketing site that
                default-drops third-party cookies before consent would be embarrassing
                on inspection. Consent Mode v2 had to be wired in at the document
                head, before the GA4 script ever loads, and had to remember
                returning-visitor state to avoid re-nagging the banner.
              </p>
              <p>
                And the lead pipeline needed to convert without a heavy CMS or CRM
                install — the client wanted to own the contact list and route inbound
                inquiries directly through SendGrid, not through a middleware SaaS
                that would sit in the audit trail.
              </p>
            </div>
          </div>
        </section>

        {/* SOLUTION / KEY FEATURES */}
        <section className="case-study__section">
          <div className="container">
            <p className="case-study__section-label">The Solution</p>
            <h2 className="case-study__section-title">
              Framework-first messaging, consent-first analytics, direct-to-SendGrid leads.
            </h2>
            <p className="case-study__section-lead">
              A static multi-page site — vanilla HTML, CSS, and JavaScript — with
              no framework wrapper, no CMS, and no analytics middleware. Every
              choice traded convenience for auditability.
            </p>

            <div className="case-study__features">
              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiCheckSquare /></span>
                <h3 className="case-study__feature-title">Framework-Aligned Messaging</h3>
                <p className="case-study__feature-body">
                  Copy and metadata targeted at NIST AI RMF, ISO 42001, SOC 2, GDPR,
                  and HIPAA buyers. Structured-data <code>Organization</code> JSON-LD
                  makes the alignment machine-readable for AI-search citations, not
                  just human readers.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiEye /></span>
                <h3 className="case-study__feature-title">GA4 + Consent Mode v2</h3>
                <p className="case-study__feature-body">
                  Google Analytics 4 wired with Consent Mode v2 at the document
                  head. All storage categories default to <code>denied</code> before
                  gtag loads, upgraded to <code>granted</code> only on cookie-banner
                  accept, with returning-visitor state persisted to localStorage.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiLayers /></span>
                <h3 className="case-study__feature-title">Multi-Page Static Architecture</h3>
                <p className="case-study__feature-body">
                  Index, About, Privacy, Terms, and Style Guide pages sharing a
                  unified design system, favicon SVG, and sitemap. Static hosting,
                  no framework overhead, sub-second first paint.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiSearch /></span>
                <h3 className="case-study__feature-title">Structured Data SEO</h3>
                <p className="case-study__feature-body">
                  <code>Organization</code> JSON-LD, Open Graph and Twitter card
                  metadata, canonical URLs, and semantic markup — so the site
                  indexes cleanly for traditional search and provides a citation
                  surface for AI-driven search tools.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiMail /></span>
                <h3 className="case-study__feature-title">SendGrid Lead Pipeline</h3>
                <p className="case-study__feature-body">
                  Contact-capture modal wired directly to the client's SendGrid
                  API for inbound lead intake — no middleware SaaS in the audit
                  trail, plus a mailto fallback for browsers with JavaScript
                  disabled.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiShield /></span>
                <h3 className="case-study__feature-title">Brand + Design System</h3>
                <p className="case-study__feature-body">
                  Dark-first design language with a mint accent, SVG favicon, and
                  a public Style Guide page so future collateral (decks, one-
                  pagers, product UI) can reuse the same tokens without drift.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* TECH STACK */}
        <section className="case-study__section" style={{ background: 'var(--color-gray-100)' }}>
          <div className="container">
            <p className="case-study__section-label">The Stack</p>
            <h2 className="case-study__section-title">Vanilla web, no framework tax.</h2>
            <p className="case-study__section-lead">
              For a marketing site that has to load in one paint and pass a privacy
              audit, plain HTML/CSS/JS beats every framework choice on the axes
              that matter: bundle size, time-to-interactive, and vendor lock-in.
            </p>

            <div className="case-study__tech">
              <span className="case-study__tech-chip">HTML5</span>
              <span className="case-study__tech-chip">CSS3</span>
              <span className="case-study__tech-chip">JavaScript</span>
              <span className="case-study__tech-chip">GA4</span>
              <span className="case-study__tech-chip">Consent Mode v2</span>
              <span className="case-study__tech-chip">JSON-LD (Organization)</span>
              <span className="case-study__tech-chip">Open Graph</span>
              <span className="case-study__tech-chip">Twitter Cards</span>
              <span className="case-study__tech-chip">SendGrid</span>
              <span className="case-study__tech-chip">Sitemap XML</span>
              <span className="case-study__tech-chip">SVG Favicon</span>
            </div>
          </div>
        </section>

        {/* RESULTS */}
        <section className="case-study__section case-study__section--dark">
          <div className="container">
            <p className="case-study__section-label">The Results</p>
            <h2 className="case-study__section-title">Shipped, indexed, audit-ready.</h2>
            <p className="case-study__section-lead" style={{ color: 'rgba(255,255,255,0.75)' }}>
              AI Shadow is live at aishadow.ai — indexed by Google, framework-aligned
              for enterprise procurement reviews, and instrumented so the client can
              actually see who's engaging with what.
            </p>

            <div className="case-study__stats">
              <div className="case-study__stat">
                <p className="case-study__stat-value">5 frameworks</p>
                <p className="case-study__stat-label">Compliance angles woven into the site's messaging</p>
              </div>
              <div className="case-study__stat">
                <p className="case-study__stat-value">0 middleware</p>
                <p className="case-study__stat-label">SaaS layers between the lead form and SendGrid</p>
              </div>
              <div className="case-study__stat">
                <p className="case-study__stat-value">Consent-first</p>
                <p className="case-study__stat-label">Storage denied by default until banner-accept</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="case-study__section">
          <div className="container">
            <div className="case-study__cta">
              <h2 className="case-study__cta-title">Positioning a new category?</h2>
              <p className="case-study__cta-text">
                Compliance-fluent messaging, consent-first analytics, and a
                lead pipeline that keeps the client in control — we design and
                ship enterprise marketing sites like AI Shadow end-to-end.
              </p>
              <Link to="/contact" className="case-study__cta-btn">
                Start Your Project <FiArrowRight />
              </Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default AIShadowCaseStudy;
